<template>
  <div :class="wrapperClasses">
    <Tooltip
      ref="tooltipRef"
      v-bind="dynamicProps"
      :arrow="false"
      :placement="effectivePlacement"
      :trigger="triggerActions"
      transition-name="fade"
      :mouse-enter-delay="testMode ? 0 : props.mouseEnterDelay"
      :mouse-leave-delay="props.mouseLeaveDelay"
      :overlay-class-name="wrapperClasses"
      :get-popup-container="getPopupContainer"
      :auto-adjust-overflow="autoAdjustOverflow"
      :destroy-tooltip-on-hide="false"
      :align="effectiveAlign"
      class="partner-population-tooltips"
      @open-change="onVisibleChange"
    >
      <!-- default slot is for the item that triggers a popover -->
      <div
        ref="triggerRef"
        data-testid="bitts-tooltip-trigger"
        class="bitts-tooltip-trigger"
        :class="defaultSlotClasses"
        :tabindex="hasFocusableChild ? undefined : 0"
        :aria-describedby="
          $slots.title && !hasFocusableChild ? tooltipId : undefined
        "
        @pointerdown="onPointerdown"
        @focusin="onFocusin"
        @mouseenter="onMouseenter"
        @mouseleave="onMouseleave"
      >
        <slot />
      </div>
      <!-- title slot is for tooltip content -->
      <template v-if="$slots.title" #title>
        <TestHook>
          <div
            :id="tooltipId"
            ref="titleRef"
            :role="includeCta || titleHasInteractive ? undefined : 'tooltip'"
          >
            <slot name="title" />
            <component :is="anchorOrDiv" v-if="includeCta" v-bind="anchorProps">
              <BittsButton
                :text="buttonText || 'Learn More'"
                type="white"
                :left-icon="ctaIcon"
                data-testid="bitts-tooltip-button"
                class="c-bitts-tooltip__button"
                size="x-small"
                @click="onCtaClicked($event)"
              />
            </component>
          </div>
        </TestHook>
      </template>
    </Tooltip>
  </div>
</template>

<script setup lang="ts">
import { useEventListener, useMutationObserver } from '@vueuse/core';
import { Tooltip } from 'ant-design-vue';
import { computed, onMounted, ref, toRef, useId, useSlots, watch } from 'vue';

import BittsButton from '../BittsButton/BittsButton.vue';
import {
  AlignOverflow,
  useViewportPlacement,
  withViewportOverflow,
} from '../composables/useViewportPlacement';
import TestHook from '../TestHook.vue';
import { BittsIcon, Placement, PlacementType } from '../types';

export type TooltipAlign = {
  offset?: [number, number];
  overflow?: AlignOverflow;
};

export type Props = {
  overlayClass?: string;
  ctaIcon?: BittsIcon;
  buttonText?: string;
  includeCta?: boolean;
  placement?: PlacementType;
  learnMoreLink?: string | null;
  mountToBody?: boolean;
  showTooltip?: boolean | null;
  stopPropagation?: boolean;
  defaultSlotClasses?: string;
  mouseEnterDelay?: number;
  mouseLeaveDelay?: number;
  autoAdjustOverflow?: boolean;
  align?: TooltipAlign;
};

const props = withDefaults(defineProps<Props>(), {
  overlayClass: '',
  ctaIcon: null,
  buttonText: '',
  includeCta: false,
  placement: Placement.Top,
  learnMoreLink: null,
  mountToBody: false,
  showTooltip: null,
  stopPropagation: false,
  defaultSlotClasses: '',
  mouseEnterDelay: 0.5,
  mouseLeaveDelay: 0.1,
  autoAdjustOverflow: true,
  align: undefined,
});

const emit = defineEmits(['menu-visible', 'cta-clicked']);

const anchorOrDiv = computed(() => (props.learnMoreLink ? 'a' : 'div'));

const anchorProps = computed(() =>
  props.learnMoreLink
    ? {
        href: props.learnMoreLink,
        target: '_blank',
      }
    : {},
);

const wrapperClasses = computed(() => {
  const classes = ['bitts-tooltip'];
  if (props.overlayClass) classes.push(props.overlayClass);
  return classes.join(' ');
});

const testMode = computed(() => import.meta.env.MODE === 'test');

const tooltipRef = ref<{ getPopupDomNode(): Node | null } | null>(null);
const isSuppressed = ref(false);
const isAntdOpen = ref(false);

const triggerActions = ['hover'] as ('hover' | 'focus')[];
const tooltipId = useId();
const isFocusOpen = ref(false);
const escDismissed = ref(false);
const hoverDismissed = ref(false);

const slots = useSlots();
const triggerRef = ref<HTMLElement | null>(null);
const titleRef = ref<HTMLElement | null>(null);
const hasFocusableChild = ref(false);
const titleHasInteractive = ref(false);
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

function firstFocusable(el: HTMLElement | null) {
  return el?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? null;
}

function syncAccessibility() {
  const focusableChild = firstFocusable(triggerRef.value);
  hasFocusableChild.value = !!focusableChild;
  titleHasInteractive.value = !!firstFocusable(titleRef.value);

  if (!focusableChild || !slots.title) return;
  const existing = focusableChild.getAttribute('aria-describedby');
  if (existing && existing !== tooltipId) return;
  const label = focusableChild.getAttribute('aria-label')?.trim();
  const titleText = titleRef.value?.textContent?.trim();
  const labelDuplicatesTitle = !!label && !!titleText && label === titleText;
  if (labelDuplicatesTitle) {
    if (existing === tooltipId) {
      focusableChild.removeAttribute('aria-describedby');
    }
  } else {
    focusableChild.setAttribute('aria-describedby', tooltipId);
  }
}

onMounted(syncAccessibility);
watch(titleRef, syncAccessibility, { flush: 'post' });
useMutationObserver(triggerRef, syncAccessibility, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['disabled'],
});

function getPopupDomNode(): HTMLElement | null {
  const node = tooltipRef.value?.getPopupDomNode();
  return node instanceof HTMLElement ? node : null;
}

const forcedClosed = computed(
  () => isSuppressed.value || escDismissed.value || hoverDismissed.value,
);

const isOpen = computed(() => {
  if (forcedClosed.value) return false;
  if (props.showTooltip !== null) return props.showTooltip;
  if (!slots.title) return false;
  return isAntdOpen.value || isFocusOpen.value;
});

function overlayContains(node: Node | null) {
  if (!node) return false;
  return (
    !!triggerRef.value?.contains(node) || !!getPopupDomNode()?.contains(node)
  );
}

useEventListener(
  () => (isOpen.value ? document : undefined),
  'keydown',
  (event: KeyboardEvent) => {
    if (event.key !== 'Escape') return;
    if (props.showTooltip !== null) return;
    if (overlayContains(document.activeElement)) {
      escDismissed.value = true;
    } else {
      hoverDismissed.value = true;
    }
  },
  { capture: true },
);

let focusFromPointer = false;
function onPointerdown() {
  focusFromPointer = true;
  window.setTimeout(() => {
    focusFromPointer = false;
  }, 0);
}

function onFocusin() {
  if (focusFromPointer) {
    focusFromPointer = false;
    return;
  }
  if (!slots.title || isSuppressed.value) return;
  if (!isFocusOpen.value) escDismissed.value = false;
  hoverDismissed.value = false;
  isFocusOpen.value = true;
}

function onMouseenter() {
  hoverDismissed.value = false;
}

function onMouseleave() {
  hoverDismissed.value = false;
  if (!isFocusOpen.value) escDismissed.value = false;
}

useEventListener(
  () => (isFocusOpen.value ? document : undefined),
  'focusout',
  () =>
    window.setTimeout(() => {
      if (overlayContains(document.activeElement)) return;
      isFocusOpen.value = false;
      escDismissed.value = false;
    }, 0),
  { capture: true },
);

const { effectivePlacement, getPopupContainer, onShow, onHide } =
  useViewportPlacement(
    toRef(props, 'placement'),
    toRef(props, 'mountToBody'),
    getPopupDomNode,
  );

function onVisibleChange(visible: boolean) {
  isAntdOpen.value = visible;
}

watch(
  isOpen,
  (open, wasOpen) => {
    if (wasOpen !== undefined && open !== wasOpen) {
      emit('menu-visible', { visible: open });
    }
    open ? onShow() : onHide();
  },
  { immediate: true },
);

const effectiveAlign = computed(() =>
  props.mountToBody ? withViewportOverflow(props.align) : props.align,
);

const dynamicProps = computed(() => {
  if (forcedClosed.value) return { open: false };
  if (props.showTooltip !== null) return { open: props.showTooltip };
  if (isFocusOpen.value && slots.title) return { open: true };
  return {};
});

function onCtaClicked(event: Event) {
  if (props.stopPropagation) event.stopPropagation();
  isFocusOpen.value = false;
  isSuppressed.value = true;
  emit('cta-clicked');
  window.setTimeout(() => {
    isSuppressed.value = false;
  }, 300);
}
</script>

<style lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.c-bitts-tooltip__button {
  @apply w-full my-4;
}
.bitts-tooltip {
  .ant-tooltip {
    font-family: inherit;
    max-width: 280px !important;
  }
  .ant-tooltip-inner {
    @apply text-sm min-h-0 px-8 py-4 bg-neutral-background-tooltip rounded-bts-md;
    opacity: 90% !important;
  }
  &.ant-tooltip-placement-top,
  &.ant-tooltip-placement-topLeft,
  &.ant-tooltip-placement-topRight {
    padding-bottom: 0;
  }
  &.ant-tooltip-placement-right,
  &.ant-tooltip-placement-rightTop,
  &.ant-tooltip-placement-rightBottom {
    padding-left: 0;
  }
  &.ant-tooltip-placement-bottom,
  &.ant-tooltip-placement-bottomLeft,
  &.ant-tooltip-placement-bottomRight {
    padding-top: 0;
  }
  &.ant-tooltip-placement-left,
  &.ant-tooltip-placement-leftTop,
  &.ant-tooltip-placement-leftBottom {
    padding-right: 0;
  }
}
</style>
